import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { isEmpty } from 'ramda'
import * as yup from 'yup'
import { useInterval } from 'react-use'
import { useDispatch, useSelector } from 'react-redux'

import { apiStateSelectors } from '../../../../api/apiState'
import { walletApiEvents, walletApiSelectors } from 'modules/api/wallet'
import { FormLoader, TextField, TextInput } from '../../../../../views/components'
import { walletTransferEvents } from '../../state'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

const validationSchema = yup.object().shape({
  from: yup.string().required('Required.'),
})

const BulkTransferReferenceLookup = ({ onSuccess }: any) => {
  const dispatch = useDispatch()
  const [wallet, setWallet] = useState({ from: null })
  const [lookupStarted, setLookupStarted] = useState(false)
  const isLookingUpWallet = useSelector(state =>
    apiStateSelectors.isLoading(state)([walletApiEvents.referenceLookup_request]),
  )

  const getReferenceLookupByReference = useSelector(walletApiSelectors.getReferenceLookupByReference)
  const getGlobalBalanceAsset = useSelector(walletApiSelectors.getGlobalBalanceAsset)
  const fromWallet: $TSFixMe = getReferenceLookupByReference(wallet.from)
  // Show (and poll) the wallet the bulk transfer will actually debit: the owner's consolidated
  // wallet when they have one, otherwise the NAL the lookup returned.
  const fromRef = walletApiSelectors.spendableWalletRef(fromWallet)
  const fromBalance = getGlobalBalanceAsset(fromRef) || { balance: 0 }
  const fromWalletExists = useSelector(state => walletApiSelectors.walletExists(state)(fromRef))
  // The lookup came back without a wallet for the reference
  const lookupFailed = lookupStarted && !isLookingUpWallet && !fromWallet

  useEffect(() => {
    if (wallet.from) {
      dispatch(walletApiEvents.referenceLookup_request({ reference: wallet.from }))
    }
  }, [dispatch, wallet])

  useEffect(() => {
    if (isLookingUpWallet) setLookupStarted(true)
  }, [isLookingUpWallet])

  useEffect(() => {
    if (fromWallet && fromBalance && fromWalletExists) {
      onSuccess({ fromWallet, fromBalance })
    }
  }, [onSuccess, fromWallet, fromBalance, fromWalletExists])

  // poll balances in case of wallet creation. A reference that doesn't resolve leaves
  // `fromWallet` undefined, so only poll once the lookup has actually returned a wallet.
  useInterval(
    () => {
      if (!fromRef) return
      dispatch(walletApiEvents.balance_request({ partyId: fromRef }))
    },
    fromRef && !fromWalletExists ? 2000 : null,
  )

  return (
    <Formik
      initialValues={{ from: '' }}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        setLookupStarted(false)
        setWallet(values)
      }}
    >
      {({ handleChange, handleSubmit, submitCount, errors, values }) => {
        const searchFormLoaderState = isLookingUpWallet
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) || lookupFailed
          ? 'error'
          : undefined

        return (
          <FormLoader
            onSubmit={handleSubmit}
            state={searchFormLoaderState}
            submittingText="Fetching wallet for confirmation"
            errorText={lookupFailed ? 'Wallet reference not found' : 'Resolve errors'}
            buttonProps={{ children: 'Lookup Wallet' }}
          >
            <div className="rbn--row" style={{ marginBottom: '20px' }}>
              <div className="rbn--col rbn-col-md-4">
                <TextField
                  inputComponent={
                    <TextInput type={TextFieldTypes.text} name="from" value={values.from} onChange={handleChange} />
                  }
                  label="From Wallet"
                  error={errors.from}
                />
              </div>
              <div className="rbn--col rbn-col-md-4"></div>
              <div className="rbn--col rbn-col-md-4"></div>
            </div>
          </FormLoader>
        )
      }}
    </Formik>
  )
}

export default BulkTransferReferenceLookup
